import { P as PlaybackState, a as PausedReason, W as WaitingReason, C as CoreEvent, b as PlayingChangeType } from './event.js';
import { g as getLogger } from './logger.js';
import { TypeState } from 'typestate';
import { DestroyableSet } from '@nrk/destroyable';

/**
 * Flat list of playback states for a non-hierarchical state machine.
 */

let AdapterState;

(function (AdapterState) {
  AdapterState["Idle"] = "idle";
  AdapterState["Playing"] = "playing";
  AdapterState["Paused"] = "paused";
  AdapterState["AutoplayBlocked"] = "autoplayblocked";
  AdapterState["Broken"] = "broken";
  AdapterState["Starting"] = "starting";
  AdapterState["Seeking"] = "seeking";
  AdapterState["Rebuffering"] = "rebuffering";
  AdapterState["PausedSeeking"] = "paused-seeking";
  AdapterState["Ended"] = "ended";
})(AdapterState || (AdapterState = {}));

/**
 * Mapping from the flat list to the playback and waiting states.
 */
function adapterToPlaybackState(adapterState) {
  const map = {
    [AdapterState.Idle]: {
      playbackState: PlaybackState.Idle
    },
    [AdapterState.Playing]: {
      playbackState: PlaybackState.Playing
    },
    [AdapterState.Paused]: {
      playbackState: PlaybackState.Paused
    },
    [AdapterState.AutoplayBlocked]: {
      playbackState: PlaybackState.Paused,
      pausedReason: PausedReason.AutoplayBlocked
    },
    [AdapterState.Broken]: {
      playbackState: PlaybackState.Broken
    },
    [AdapterState.Starting]: {
      playbackState: PlaybackState.Playing,
      waitingReason: WaitingReason.Starting
    },
    [AdapterState.Seeking]: {
      playbackState: PlaybackState.Playing,
      waitingReason: WaitingReason.Seeking
    },
    [AdapterState.Rebuffering]: {
      playbackState: PlaybackState.Playing,
      waitingReason: WaitingReason.Rebuffering
    },
    [AdapterState.PausedSeeking]: {
      playbackState: PlaybackState.Paused,
      waitingReason: WaitingReason.Seeking
    },
    [AdapterState.Ended]: {
      playbackState: PlaybackState.Paused,
      pausedReason: PausedReason.Ended
    }
  };
  return map[adapterState];
}

/**
 * Add all legal adapter state transitions.
 */
function addStateTransitions(stateMachine) {
  stateMachine.from(AdapterState.Idle).to( // Play is triggered, and the player needs to load, maybe seek, buffer,
  // before the media actually starts playing.
  AdapterState.Starting, // The media is found broken or unavailable when preloading, before play is
  // triggered.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Starting).to( // The happy path. Playback starts normally after being loaded/buffered.
  AdapterState.Playing, // Pause is triggered before playback has a chance to start. If play has
  // not actually been called on the media element yet, it must not be
  // called.
  AdapterState.Paused, // Playback couldn't start because of autoplay policy.
  AdapterState.AutoplayBlocked, // The media is found broken or unavailable after play was triggered,
  // before playback has started.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Playing).to( // The user seeks to another position while the media is playing.
  AdapterState.Seeking, // The network is slow, causing rebuffering.
  AdapterState.Rebuffering, // Pause is triggered.
  AdapterState.Paused, // Playback was paused bacause an autoplayed session was unmuted without
  // user interaction.
  AdapterState.AutoplayBlocked, // The end of on-demand media is reached.
  AdapterState.Ended, // Playback stops unexpectedly and can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Seeking).to( // The player resumes playing after a seek.
  AdapterState.Playing, // Pause is triggered while the player is seeking. The player could
  // continue to rebuffer in the background, but it must not start playing
  // afterwards.
  AdapterState.Paused, // The seek was to the end of an on-demand stream.
  AdapterState.Ended, // Something broke and playback can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Rebuffering).to( // The player resumes playing after rebuffering.
  AdapterState.Playing, // The user seeks to another position during rebuffering.
  AdapterState.Seeking, // Pause is triggered while rebuffering. The player could continue to
  // rebuffer in the background, but it must not start playing afterwards.
  AdapterState.Paused, // The rebuffering happened just at the end of an on-demand stream.
  //
  // Unclear: Will this actually happen? Does it matter?
  AdapterState.Ended, // Something broke and playback can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Paused).to( // Play is triggered while paused and playback starts immediately.
  AdapterState.Playing, // Play is triggered while paused but playback doesn't start immediately.
  AdapterState.Rebuffering, // Seeking is triggered while paused.
  AdapterState.PausedSeeking, // A pause event was initially not correctly detected as ended, but being
  // stuck in paused is no good. So allow going from paused to paused with
  // ended.
  AdapterState.Ended, // Something broke and playback can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.AutoplayBlocked).to( // Play is triggered and playback starts immediately.
  AdapterState.Playing, // Play is triggered but playback doesn't start immediately.
  AdapterState.Rebuffering, // Seeking is triggered while blocked by autoplay policy (this ok?).
  AdapterState.PausedSeeking, // Something broke and playback can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.PausedSeeking).to( // Play is triggered while seeking.
  AdapterState.Seeking, // Seeking ended while still paused. Only allowed if the previous state was
  // AdapterState.Paused.
  AdapterState.Paused, // Seeking ended while still blocked by autoplay policy. Only allowed if
  // the previous state was AdapterState.AutoplayBlocked.
  AdapterState.AutoplayBlocked, // The seek was to the end of an on-demand stream.
  AdapterState.Ended, // Something broke and playback can't be resumed.
  AdapterState.Broken);
  stateMachine.from(AdapterState.Ended).to( // Play is triggered (replay) and playback starts immediately.
  AdapterState.Playing, // Play is triggered (replay) but playback doesn't start immediately.
  AdapterState.Rebuffering, // Seeking is triggered while paused.
  AdapterState.PausedSeeking);
}

/**
 * Send state change events to the public event emitter. Follows the same order
 * as the legal state transitions in stateTransitions.ts.
 *
 * Only state can be resolved here, no data, so the broken event can't provide
 * any error data.
 */
function addStateChangeEvents(stateMachine, emitter, dataProvider) {
  // Helper function for being able to structure the transition listeners like
  // below.
  function onTransition(from, to, listener) {
    stateMachine.on(to, actualFrom => {
      if (from === actualFrom) {
        listener();
      }
    });
  } // After ended, a playing change is a replay, but any number of seek jumps
  // could happen between. Use a variable to keep track.


  let playIsReplay = false;

  const emitPlaying = () => {
    if (playIsReplay) {
      playIsReplay = false;
      emitter.emit(CoreEvent.PlayingChange, {
        isPlaying: true,
        type: PlayingChangeType.Replay
      });
    } else {
      emitter.emit(CoreEvent.PlayingChange, {
        isPlaying: true
      });
    }
  };

  onTransition(AdapterState.Idle, AdapterState.Starting, () => {
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: true,
      type: PlayingChangeType.Starting
    });
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Starting
    });
  });
  onTransition(AdapterState.Idle, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Starting, AdapterState.Playing, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Starting
    });
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.Starting, AdapterState.Paused, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Starting
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
  });
  onTransition(AdapterState.Starting, AdapterState.AutoplayBlocked, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Starting
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.AutoplayBlocked
    });
  });
  onTransition(AdapterState.Starting, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Starting
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Playing, AdapterState.Seeking, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.Playing, AdapterState.Rebuffering, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Rebuffering
    });
  });
  onTransition(AdapterState.Playing, AdapterState.Paused, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
  });
  onTransition(AdapterState.Playing, AdapterState.AutoplayBlocked, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.AutoplayBlocked
    });
  });
  onTransition(AdapterState.Playing, AdapterState.Ended, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.Ended
    });
  });
  onTransition(AdapterState.Playing, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: false,
      time: dataProvider.getStopTime()
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Seeking, AdapterState.Playing, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.Seeking, AdapterState.Paused, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
  });
  onTransition(AdapterState.Seeking, AdapterState.Ended, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.Ended
    });
  });
  onTransition(AdapterState.Seeking, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Rebuffering, AdapterState.Playing, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Rebuffering
    });
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.Rebuffering, AdapterState.Seeking, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.Rebuffering, AdapterState.Paused, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Rebuffering
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
  });
  onTransition(AdapterState.Rebuffering, AdapterState.Ended, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Rebuffering
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.Ended
    });
  });
  onTransition(AdapterState.Rebuffering, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Rebuffering
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false
    });
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Paused, AdapterState.Playing, () => {
    emitPlaying();
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.Paused, AdapterState.Rebuffering, () => {
    emitPlaying();
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Rebuffering
    });
  });
  onTransition(AdapterState.Paused, AdapterState.PausedSeeking, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.Paused, AdapterState.Ended, () => {
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.Ended
    });
  });
  onTransition(AdapterState.Paused, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.AutoplayBlocked, AdapterState.Playing, () => {
    emitPlaying();
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.AutoplayBlocked, AdapterState.Rebuffering, () => {
    emitPlaying();
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Rebuffering
    });
  });
  onTransition(AdapterState.AutoplayBlocked, AdapterState.PausedSeeking, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.AutoplayBlocked, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.PausedSeeking, AdapterState.Seeking, () => {
    emitPlaying();
  });
  onTransition(AdapterState.PausedSeeking, AdapterState.Paused, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.PausedSeeking, AdapterState.AutoplayBlocked, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
  });
  onTransition(AdapterState.PausedSeeking, AdapterState.Ended, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.PlayingChange, {
      isPlaying: false,
      pausedReason: PausedReason.Ended
    });
  });
  onTransition(AdapterState.PausedSeeking, AdapterState.Broken, () => {
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: false,
      reason: WaitingReason.Seeking
    });
    emitter.emit(CoreEvent.Broken, {
      error: dataProvider.getError()
    });
  });
  onTransition(AdapterState.Ended, AdapterState.Playing, () => {
    playIsReplay = true;
    emitPlaying();
    emitter.emit(CoreEvent.TimeRunningChange, {
      isTimeRunning: true,
      time: dataProvider.getStartTime()
    });
  });
  onTransition(AdapterState.Ended, AdapterState.Rebuffering, () => {
    playIsReplay = true;
    emitPlaying();
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Rebuffering
    });
  });
  onTransition(AdapterState.Ended, AdapterState.PausedSeeking, () => {
    playIsReplay = true;
    emitter.emit(CoreEvent.WaitingChange, {
      isWaiting: true,
      reason: WaitingReason.Seeking
    });
  });
}

const logger$2 = getLogger('AdapterFSM');

/**
 * Convenience method for creating a new adapter state machine.
 */
function createAdapterStateMachine(emitter, dataProvider) {
  const stateMachine = new TypeState.FiniteStateMachine(AdapterState.Idle);
  addStateTransitions(stateMachine);
  addStateChangeEvents(stateMachine, emitter, dataProvider); // eslint-disable-next-line @typescript-eslint/unbound-method

  stateMachine.onTransition = (from, to) => {
    logger$2.log(`Transitioned from ${from} to ${to}.`);
  };

  stateMachine.onInvalidTransition((from, to) => {
    logger$2.log(`Blocked adapter state transition from ${from} to ${to}.`);
    return true; // Handled. Don't throw error.
  });
  return stateMachine;
}

const logger$1 = getLogger('AdapterBaseAbstract');
/**
 * Prepares the most common and equal needs for all adapter implementations.
 */

class AdapterBaseAbstract extends DestroyableSet {
  /**
   * Must be set before destroying the adapter from within, and only when
   * destroying the adapter from within. PlaybackSession listens for the
   * adapter 'destroy' event and adds the reason to the SessionClosed event.
   */

  /**
   * True if playback ever succeeded.
   */
  constructor(eventEmitter) {
    super();
    this.destroyedReason = void 0;
    this.eventEmitter = void 0;
    this.fatalError = void 0;
    this.stateMachine = void 0;
    this.subtitleControls = void 0;
    this.playbackStarted = false;
    this.eventEmitter = eventEmitter;
    this.stateMachine = createAdapterStateMachine(eventEmitter, {
      getError: this.getError.bind(this),
      getStartTime: this.getTimeStartedPosition.bind(this),
      getStopTime: this.getTimeStoppedPosition.bind(this)
    }); // eslint-disable-next-line @typescript-eslint/unbound-method

    const previousTransitionHandler = this.stateMachine.onTransition;

    this.stateMachine.onTransition = (from, to) => {
      if (from === AdapterState.Playing && this.fatalError !== undefined) {
        if (to !== AdapterState.Broken && to !== AdapterState.Ended) {
          logger$1.log('Going to broken as playback halted after a fatal error.');
          this.stateMachine.go(AdapterState.Broken);
        }
      } else if (to === AdapterState.Playing) {
        this.playbackStarted = true;
      }

      previousTransitionHandler(from, to);
    };
  }

  // Translate adapter states to session states.
  getState() {
    return adapterToPlaybackState(this.stateMachine.currentState).playbackState;
  }

  getWaitingReason() {
    return adapterToPlaybackState(this.stateMachine.currentState).waitingReason;
  }

  getPausedReason() {
    return adapterToPlaybackState(this.stateMachine.currentState).pausedReason;
  }

  getError() {
    return this.fatalError;
  } // Proxy subtitle controls to separate implementation.


  setNoSubtitles(options) {
    var _this$subtitleControl;

    (_this$subtitleControl = this.subtitleControls) == null ? void 0 : _this$subtitleControl.setNoSubtitles(options);
  }

  setDefaultSubtitles() {
    var _this$subtitleControl2;

    (_this$subtitleControl2 = this.subtitleControls) == null ? void 0 : _this$subtitleControl2.setDefaultSubtitles();
  }

  setAutoSubtitles() {
    var _this$subtitleControl3;

    (_this$subtitleControl3 = this.subtitleControls) == null ? void 0 : _this$subtitleControl3.setAutoSubtitles();
  }

  setActiveSubtitles(id, options) {
    var _this$subtitleControl4;

    (_this$subtitleControl4 = this.subtitleControls) == null ? void 0 : _this$subtitleControl4.setActiveSubtitles(id, options);
  }

  getActiveSubtitles() {
    var _this$subtitleControl5;

    return (_this$subtitleControl5 = this.subtitleControls) == null ? void 0 : _this$subtitleControl5.getActiveSubtitles();
  }

  getAvailableSubtitles() {
    var _this$subtitleControl6, _this$subtitleControl7;

    return (_this$subtitleControl6 = (_this$subtitleControl7 = this.subtitleControls) == null ? void 0 : _this$subtitleControl7.getAvailableSubtitles()) != null ? _this$subtitleControl6 : [];
  }

  enableNativeSubtitlesDisplay() {
    var _this$subtitleControl8;

    (_this$subtitleControl8 = this.subtitleControls) == null ? void 0 : _this$subtitleControl8.enableNativeSubtitlesDisplay();
  }

  disableNativeSubtitlesDisplay() {
    var _this$subtitleControl9;

    (_this$subtitleControl9 = this.subtitleControls) == null ? void 0 : _this$subtitleControl9.disableNativeSubtitlesDisplay();
  }

  isNativeSubtitlesDisplayEnabled() {
    var _this$subtitleControl10, _this$subtitleControl11;

    return (_this$subtitleControl10 = (_this$subtitleControl11 = this.subtitleControls) == null ? void 0 : _this$subtitleControl11.isNativeSubtitlesDisplayEnabled()) != null ? _this$subtitleControl10 : false;
  } // Error handling.


  handleError(error) {
    if (!error.fatal) {
      logger$1.log('Got non-fatal error:', error, error.cause);
      return;
    }

    if (this.fatalError !== undefined) {
      logger$1.log('Got follow-up fatal error:', error, error.cause);
      return;
    }

    logger$1.log('Got fatal error:', error, error.cause);
    this.fatalError = error; // Go to broken state, but not if time is running. Then wait until playback
    // halts.

    if (this.stateMachine.is(AdapterState.Playing)) {
      logger$1.log('Saving fatal error for later when time stops.');
    } else {
      logger$1.log('Time is not running. Changing to broken.');
      this.stateMachine.go(AdapterState.Broken);
    }
  } // Destroy.


  destroy() {
    var _this$subtitleControl12;

    (_this$subtitleControl12 = this.subtitleControls) == null ? void 0 : _this$subtitleControl12.destroy == null ? void 0 : _this$subtitleControl12.destroy();
    super.destroy();
  }

}

const logger = getLogger('AdapterTimeBase');

/**
 * Implements time-, time ranges- and live calculations. Basically all the
 * "getters" related to time and live states, and seek, except
 * `getCurrentTime()` and `seekTo()`.
 *
 * Subclass must call `handleMediaRangeUpdate()` when the media state is known
 * and updated. See `handleMediaRangeUpdate()` for details.
 */
class AdapterBaseTime extends AdapterBaseAbstract {
  /**
   * The current range of the media, as reported via
   * `this.handleMediaRangeUpdate()`, not directly.
   */

  /**
   * The UTC date/time of the stream content at position 0, in seconds since
   * epoch. Used for calculating the date/time of any position in the stream.
   *
   * It should be set via `this.handleMediaRangeUpdate()`, not directly.
   */

  /**
   * True if the media was live when initially loaded.
   */

  /**
   * True if the media range is no longer expected to be updated, if `isLive`
   * is true.
   */

  /**
   * The UTC date/time (wall clock) of the client, and the position into the
   * media range that playback should not pass in order to avoid rebuffering,
   * at the time live sliding started.
   *
   * `clientDateTime` is the number of seconds since epoch.
   *
   * `livePosition` might be negative if there are fewer than the ideal buffer
   * size segments at start. It might be `undefined` if the browser doesn't
   * provide a usable number (`Infinity`).
   */

  /**
   * If the media range moves at both ends (assumed to move synchronously),
   * then the sliding window duration decides the duration of the scrubber
   * range over the media range, normally ending at the live position. It might
   * be larger than the media range, creating a non-seekable hole at the
   * beginning (left end) of the scrubber.
   *
   * If it's undefined, then the media range is growing. If it's 0, then
   * seeking is not supported, regardless of stream type.
   */
  constructor(eventEmitter, liveOptions) {
    super(eventEmitter);
    this.liveOptions = void 0;
    this.mediaRange = void 0;
    this.programDateTimeAtPosZero = void 0;
    this.isLive = void 0;
    this.isLiveEnded = void 0;
    this.slidingStart = void 0;
    this.slidingWindowDuration = void 0;
    this.liveOptions = liveOptions;
    this.slidingWindowDuration = liveOptions == null ? void 0 : liveOptions.slidingWindowDuration;
  }

  isLiveStream() {
    var _this$isLive;

    return (_this$isLive = this.isLive) != null ? _this$isLive : false;
  }

  isLiveStreamEnded() {
    var _this$isLiveEnded;

    return (_this$isLiveEnded = this.isLiveEnded) != null ? _this$isLiveEnded : false;
  }

  isSeekable() {
    return this.slidingWindowDuration !== 0;
  }

  getCurrentTimeLive() {
    return this.positionToLiveTime(this.getCurrentTime());
  }

  seekToRelativeTime(relativeTime) {
    this.seekTo(this.getCurrentTime() + relativeTime);
  }

  seekToLive() {
    if (this.isLiveStream() && !this.isLiveStreamEnded()) {
      const seekableRange = this.getSeekableRange(); // Don't seek if it moves the playhead backwards.

      if (this.getCurrentTime() < seekableRange.end) {
        this.seekTo(seekableRange.end);
      }
    }
  }

  seekToLiveTime(liveTime) {
    if (this.isLiveStream()) {
      this.seekTo(this.liveTimeToPosition(liveTime));
    }
  }
  /**
   * If `programDateTimeAtPosZero` isn't set, then the output will be
   * completely wrong.
   */


  positionToLiveTime(position) {
    var _this$programDateTime;

    return new Date((((_this$programDateTime = this.programDateTimeAtPosZero) != null ? _this$programDateTime : 0) + position) * 1000);
  }
  /**
   * If `programDateTimeAtPosZero` isn't set, then the output will be
   * completely wrong.
   */


  liveTimeToPosition(liveTime) {
    var _this$programDateTime2;

    return liveTime.getTime() / 1000 - ((_this$programDateTime2 = this.programDateTimeAtPosZero) != null ? _this$programDateTime2 : 0);
  }
  /**
   * Returns the reported or the de facto available media range.
   */


  getMediaRange() {
    var _this$mediaRange;

    return (_this$mediaRange = this.mediaRange) != null ? _this$mediaRange : {
      start: 0,
      end: this.getCurrentTime()
    };
  }
  /**
   * Returns the range of the scrubber. For on-demand it's equal to the media
   * range. For live it's sliding or gradually expanding with time, depending
   * on the type of stream.
   */


  getScrubberRange() {
    var _this$slidingStart;

    const mediaRange = this.getMediaRange();

    if (!this.isLiveStream() || this.isLiveStreamEnded() || ((_this$slidingStart = this.slidingStart) == null ? void 0 : _this$slidingStart.livePosition) === undefined) {
      return mediaRange;
    } // Growing or sliding (potentially 0) buffer?


    const end = Math.max(this.getCurrentTime(), this.getLivePosition());
    return {
      start: this.slidingWindowDuration === undefined ? 0 : end - this.slidingWindowDuration,
      end
    };
  }

  getLivePosition() {
    var _this$slidingStart2;

    if (((_this$slidingStart2 = this.slidingStart) == null ? void 0 : _this$slidingStart2.livePosition) === undefined) {
      return 0;
    }

    const sliding = Date.now() / 1000 - this.slidingStart.clientDateTime;
    return this.slidingStart.livePosition + sliding;
  }
  /**
   * The seekable range is the intersection of the media and scrubber ranges.
   */


  getSeekableRange() {
    const mediaRange = this.getMediaRange();

    if (!this.isLiveStream() || this.isLiveStreamEnded()) {
      return mediaRange;
    }

    const scrubberRange = this.getScrubberRange();
    const start = Math.max(mediaRange.start, scrubberRange.start);
    const end = Math.min(mediaRange.end, scrubberRange.end); // If the ranges are disjoint, then the scrubber wins because the playhead
    // should always be inside the scrubber. It's not a feature to seek outside
    // the scrubber range, even if it's not currently possible to seek inside
    // the scrubber range. Also, if seeking isn't really supported, the media
    // range is 0-0, while the scrubber range should point to a valid position
    // in the media.

    if (start > end) {
      return scrubberRange;
    }

    return {
      start,
      end
    };
  }
  /**
   * Returns a set of values for convenient rendering of a scrubber. Can be
   * polled to produce sliding effects for live stream scrubbers and the
   * playhead (also on-demand), although it's probably better to listen to the
   * `ScrubberUpdate` events.
   */


  getScrubberValues() {
    const scrubberRange = this.getScrubberRange();
    const currentTime = this.getCurrentTime(); // Don't "visually" fall out of the scrubber range on the left side.

    const playheadPosition = Math.max(currentTime, scrubberRange.start);

    if (!this.isLiveStream() || this.programDateTimeAtPosZero === undefined) {
      return {
        startPosition: scrubberRange.start,
        startDisplayTime: scrubberRange.start,
        endPosition: scrubberRange.end,
        endDisplayTime: scrubberRange.end,
        playheadPosition,
        playheadDisplayTime: playheadPosition
      };
    }

    const startLiveTime = this.positionToLiveTime(scrubberRange.start);
    const endLiveTime = this.positionToLiveTime(scrubberRange.end);
    const playheadLiveTime = this.positionToLiveTime(playheadPosition); // TODO: Use Norwegian time zone.

    const startDisplayTime = (startLiveTime.getTime() / 1000 - startLiveTime.getTimezoneOffset() * 60) % 86400;
    const endDisplayTime = (endLiveTime.getTime() / 1000 - endLiveTime.getTimezoneOffset() * 60) % 86400;
    const playheadDisplayTime = (playheadLiveTime.getTime() / 1000 - playheadLiveTime.getTimezoneOffset() * 60) % 86400;
    return {
      startPosition: scrubberRange.start,
      startLiveTime,
      startDisplayTime,
      endPosition: scrubberRange.end,
      endLiveTime,
      endDisplayTime,
      playheadPosition,
      playheadLiveTime,
      playheadDisplayTime
    };
  }
  /**
   * Subclass must call this when the media range is known and updated, and
   * when the program date/time is known (or updated).
   */


  handleMediaRangeUpdate(mediaRange, livePosition, programDateTime) {
    var _this$slidingStart3;

    this.mediaRange = mediaRange;

    if (this.isLive === undefined) {
      this.isLive = !mediaRange.complete;
    }

    if (!this.isLive) {
      return;
    }

    let emitLiveStreamEndedEvent = false;

    if (mediaRange.complete && !this.isLiveEnded) {
      this.isLiveEnded = true;
      emitLiveStreamEndedEvent = true;
    }

    if (programDateTime !== undefined) {
      var _this$liveOptions$pro, _this$liveOptions;

      this.programDateTimeAtPosZero = programDateTime - mediaRange.start + ((_this$liveOptions$pro = (_this$liveOptions = this.liveOptions) == null ? void 0 : _this$liveOptions.programDateTimeCorrection) != null ? _this$liveOptions$pro : 0);
    }

    if (((_this$slidingStart3 = this.slidingStart) == null ? void 0 : _this$slidingStart3.livePosition) === undefined) {
      this.slidingStart = {
        livePosition,
        clientDateTime: mediaRange.timestamp
      };
    } else if (livePosition !== undefined) {
      // The current live position plus the amount of sliding until this media
      // state update gives an expected live position of this media state
      // update. If the updated live position is later/higher, then the updated
      // live position is closer to real-time than the previous. Also, if the
      // live position is way off, then use the new one anyway.
      const expectedLivePosition = this.slidingStart.livePosition + mediaRange.timestamp - this.slidingStart.clientDateTime;

      if (livePosition > expectedLivePosition || livePosition < expectedLivePosition - 60) {
        const improvement = livePosition - expectedLivePosition;
        logger.log(`Adjusting live position to be ${improvement} seconds closer to real-time.`);
        this.slidingStart = {
          livePosition,
          clientDateTime: mediaRange.timestamp
        };
      }
    } // Decide sliding window duration. Comparing to 1 instead of 0 because of
    // occational fractional errors.


    if (this.slidingWindowDuration === undefined && mediaRange.start > 1) {
      this.slidingWindowDuration = niceSlidingWindowDuration(mediaRange.end - mediaRange.start);
      logger.log(`Detected sliding window. Scrubber duration set to: ${this.slidingWindowDuration}`);
    }

    if (emitLiveStreamEndedEvent) {
      this.eventEmitter.emit(CoreEvent.LiveStreamEnded, this.getMediaRange());
    }
  }
  /**
   * Program date/time for live/event HLS streams which don't provide one.
   *
   * It takes n seconds to produce an n seconds long segment. Then the segment
   * needs to be made available in the media playlist and the playlist loaded by
   * the client. The delay is a very rough estimate. In general, it's best if the
   * player clock is slightly behind the stream time, so that navigation to a
   * date/time seeks to a position just be fore actual content.
   */


  guessHlsProgramDateTime(playlistDuration, segmentDuration) {
    var _this$liveOptions$liv, _this$liveOptions2;

    const delay = (_this$liveOptions$liv = (_this$liveOptions2 = this.liveOptions) == null ? void 0 : _this$liveOptions2.liveDelay) != null ? _this$liveOptions$liv : segmentDuration + 9;
    return Date.now() / 1000 - delay - playlistDuration;
  }

}
/**
 * Try to smooth out the sliding window range. If a few seconds is missing on a
 * "nice" duration, then round up and accept that it'll be impossible to seek
 * to the beginning of the window. Otherwise, round down to the nearest minute
 * (at least the start and end seconds will be in sync).
 *
 * Note: It doesn't take the 3 segment loading zone into account. Maybe it
 * should. Maybe it doesn't really make much of a difference.
 */

function niceSlidingWindowDuration(mediaRangeDuration) {
  const r = 60; // One revolution of the clock. Seconds in a minute. Whatever.

  const minuteBin = 5;
  const minutes = Math.floor(mediaRangeDuration / r); // Including hours.

  const seconds = mediaRangeDuration % r;
  const binnedMinutes = Math.floor(minutes / minuteBin) * minuteBin; // If we're half a revolution from a whole bin, then take it.

  if (minutes - binnedMinutes === minuteBin - 1 && seconds >= r / 2) {
    return (binnedMinutes + minuteBin) * r;
  } // Otherwise make the window a whole number of minutes.


  return minutes * r;
}

/**
 * Returns the client languages provided by the browser.
 */
function getClientLanguages() {
  return navigator.languages ? navigator.languages : navigator.language ? [navigator.language] : [];
}
/**
 * Transforms a list of language tags to be better suited for language
 * matching.
 *
 * It appends unlisted primary subtags in the order that they're resolved in
 * the listed tags. For example:
 *
 *    ['en-GB', 'sv-SE'] => ['en-GB', 'sv-SE', 'en', 'sv']
 *
 * There's also a hack for adding an 'no' tag. See comment inside. Example:
 *
 *    ['en-GB', 'nb'] => ['en-GB', 'nb', 'no', 'en']
 */

function getMatchLanguages(tags) {
  const uniqueTags = new Set(tags);
  tags.forEach(tag => uniqueTags.add(getPrimarySubtag(tag)));
  const resultTags = Array.from(uniqueTags); // Hack for Norway/NRK: Add the 'no' macro language tag just after the first
  // tag matching 'nb', 'nn' or 'se', if any. This should be removed and
  // subtitle language tags should be fixed!

  if (!uniqueTags.has('no')) {
    const firstMatch = resultTags.findIndex(tag => /^(nb|nn|se)$/.exec(tag));

    if (firstMatch >= 0) {
      resultTags.splice(firstMatch + 1, 0, 'no');
    }
  }

  return resultTags;
}
/**
 * Order a list of items by a list of preferred/ordered languages. For rough
 * matching of language tags (using primary subtags), `getMatchLanguages()`
 * should be used on the preferred languages first.
 */

function orderByLanguage(items, getItemLanguage, preferredLanguages) {
  const afterPreferredIndex = preferredLanguages.length; // Create a mapping from the index of the items' languages in the preferred
  // languages list to the items.  If an item's language doesn't exist in the
  // preferred languages, the primary subtag is used instead. If the primary
  // subtags doesn't exist in the preferred languages, then the item is placed
  // last.

  const preferredIndexMap = items.map((item, itemIndex) => {
    const language = getItemLanguage(item);
    const fullMatchIndex = preferredLanguages.indexOf(language);

    if (fullMatchIndex !== -1) {
      return [fullMatchIndex, itemIndex];
    }

    const primarySubtagIndex = preferredLanguages.indexOf(getPrimarySubtag(language));

    if (primarySubtagIndex !== -1) {
      return [primarySubtagIndex, itemIndex];
    }

    return [afterPreferredIndex, itemIndex];
  }); // Sort the index map by preferred index.

  preferredIndexMap.sort((a, b) => a[0] - b[0]); // Return the items ordered by the sorted index map.

  return preferredIndexMap.map(indexes => items[indexes[1]]);
}
/**
 * Returns the primary subtag, including any single letter prefix (i- or x-).
 *
 * Examples:
 *
 *     'en' => 'en'
 *     'en-GB' => 'en'
 *     'en-GB-oxendict' => 'en'
 *     'i-klingon' => 'i-klingon' (although 'tlh' is the proper code).
 */

function getPrimarySubtag(tag) {
  return tag.replace(/^((?:\w-)?\w+).*$/, '$1');
}

/**
 * Apply a static (and instance) interface to a class.
 *
 * Usage:
 *
 *     interface Foo {
 *       instanceMethod: () => void;
 *     }
 *
 *     interface FooStatic {
 *       new(): Foo;
 *       staticMethod: () => void;
 *     }
 *
 *     @implementsStatic<FooStatic>()
 *     class Bar {
 *       static staticMethod() {}
 *       instanceMethod() {}
 *     }
 */
function implementsStatic() {
  return constructor => {
  };
}

/**
 * Convert a HTML5 TimeRanges object to a list of TimeRange objects.
 */
function convertTimeRanges(ranges) {
  const list = [];

  for (let i = 0; i < ranges.length; ++i) {
    list.push({
      start: ranges.start(i),
      end: ranges.end(i)
    });
  }

  return list;
}
function toWithinRange(range, value) {
  if (value < range.start) {
    return range.start;
  }

  if (value > range.end) {
    return range.end;
  }

  return value;
}

export { AdapterBaseTime as A, getClientLanguages as a, AdapterState as b, adapterToPlaybackState as c, AdapterBaseAbstract as d, convertTimeRanges as e, getMatchLanguages as g, implementsStatic as i, orderByLanguage as o, toWithinRange as t };
